import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { isFloorBranch, DEPARTMENTS, floorByKey } from "@/lib/queueConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same warm champagne wash + soft gold corner glows as the in-store gold-rate TV board.
const GLOW = {
  background:
    "radial-gradient(120vh 90vh at 12% -10%, rgba(197,160,89,0.22), transparent 60%)," +
    "radial-gradient(110vh 80vh at 100% 110%, rgba(197,160,89,0.16), transparent 62%)",
};

const Label = ({ children, style }) => (
  <p
    className="uppercase tracking-[0.3em] text-[#8A7440]"
    style={{ fontSize: "1vw", ...style }}
  >
    {children}
  </p>
);

/** Customer-facing "now serving" screen for the shop floor TV. Styled to match the
 *  gold-rate TV board (light champagne, gold accents). Stores with floors
 *  (e.g. Truganina) show Gold and Silver side by side so each floor's TV is clear. */
export default function QueueDisplay() {
  const { branch, floor } = useParams();
  const store = (branch || "").toUpperCase();
  const floorKey = (floor || "").toUpperCase();
  const [board, setBoard] = useState(null);
  const [clock, setClock] = useState("");

  const load = useCallback(() => {
    axios
      .get(`${API}/public/queue/${store}`)
      .then(({ data }) => setBoard(data))
      .catch(() => {});
  }, [store]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 5000);
    const tick = setInterval(
      () =>
        setClock(
          new Date().toLocaleTimeString("en-AU", {
            timeZone: "Australia/Melbourne",
            hour: "numeric",
            minute: "2-digit",
          })
        ),
      1000
    );
    return () => {
      clearInterval(timer);
      clearInterval(tick);
    };
  }, [load]);

  const floors = isFloorBranch(store);
  const servingDetail = board?.now_serving_detail || [];
  const waitingDetail = board?.waiting_detail || [];
  const forDept = (list, key) =>
    list.filter((x) => (x.department || "") === key).map((x) => x.label || x.ticket);
  // The :floor part of the URL can be a whole floor (e.g. /TRUGANINA/first -> Silver + Bullion),
  // a single counter (e.g. /TRUGANINA/GOLD -> that counter full-screen), or nothing (every counter).
  const floorView = floors ? floorByKey(floorKey) : null;
  const soloDept =
    floors && !floorView && floorKey ? DEPARTMENTS.find((d) => d.key === floorKey) : null;
  const shownDepts = floorView
    ? floorView.departments.map((k) => DEPARTMENTS.find((d) => d.key === k)).filter(Boolean)
    : soloDept
    ? [soloDept]
    : DEPARTMENTS;
  // One counter on screen (a solo-counter URL, or a floor with a single counter) uses the big
  // full-screen layout; two or more share the screen as side-by-side panels.
  const singleBig = floors && shownDepts.length === 1;
  const headerContext = floorView
    ? `${store} · ${floorView.label}`
    : soloDept
    ? `${store} · ${soloDept.label} (${soloDept.floor})`
    : store;

  // Scale the "now serving" numbers down as more are called at once so a busy
  // floor (e.g. 9 silver tickets) never overflows its card into the waiting row.
  const servingFont = (count, big) => {
    if (big) {
      if (count <= 2) return "9vw";
      if (count <= 4) return "6vw";
      if (count <= 6) return "4.6vw";
      if (count <= 9) return "3.6vw";
      return "2.9vw";
    }
    if (count <= 2) return "6.5vw";
    if (count <= 4) return "4.4vw";
    if (count <= 6) return "3.3vw";
    if (count <= 9) return "2.7vw";
    return "2.2vw";
  };
  const servingGap = (count) => (count <= 2 ? "2.5vw" : count <= 6 ? "1.6vw" : "1vw");

  const Serving = ({ tickets, big }) => {
    const size = servingFont(tickets.length, big);
    return (
      <div
        className="mt-[1.4vh] flex flex-wrap items-center justify-center"
        style={{ gap: servingGap(tickets.length) }}
      >
        {tickets.length === 0 ? (
          <span
            className="tabular font-bold leading-none text-[#C5A059]/30"
            style={{ fontSize: big ? "9vw" : "6.5vw" }}
            data-testid="display-none"
          >
            —
          </span>
        ) : (
          tickets.map((t) => (
            <span
              key={t}
              className="tabular font-bold leading-none text-[#9A7822]"
              style={{ fontSize: size }}
              data-testid={`display-serving-${t}`}
            >
              {t}
            </span>
          ))
        )}
      </div>
    );
  };

  const Waiting = ({ tickets, big }) => (
    <div className="mt-[1.2vh] flex flex-wrap justify-center gap-[1vw]">
      {tickets.length === 0 ? (
        <span className="text-[#8A7440]/60" style={{ fontSize: big ? "1.6vw" : "1.2vw" }}>
          Nobody waiting
        </span>
      ) : (
        tickets.map((t) => (
          <span
            key={t}
            className="tabular border border-[#E4D9BF] bg-white/75 px-[1.4vw] py-[0.5vh] font-semibold text-[#2A2318] shadow-[0_1px_0_rgba(197,160,89,0.25)]"
            style={{ fontSize: big ? "2vw" : "1.5vw" }}
          >
            {t}
          </span>
        ))
      )}
    </div>
  );

  return (
    <div
      className="relative flex h-screen flex-col overflow-hidden bg-[#FBF7EF] text-[#2A2318]"
      data-testid="queue-display"
    >
      <div className="pointer-events-none absolute inset-0" style={GLOW} />

      <div className="relative flex h-full flex-col px-[3.5vw] py-[2.8vh]">
        <header className="flex items-center justify-between gap-[2vw]">
          <div className="flex items-center gap-[1.4vw]">
            <img
              src="/brand/logo.png"
              alt="Dhunna Jewellers"
              className="w-auto shrink-0 object-contain"
              style={{ height: "min(6.5vw, 12vh)", mixBlendMode: "multiply" }}
              data-testid="queue-display-logo"
            />
            <div>
              <p
                className="font-invoice uppercase leading-none tracking-[0.2em] text-[#2A2318]"
                style={{ fontSize: "2.1vw" }}
              >
                Dhunna Jewellers
              </p>
              <p
                className="mt-[0.8vh] uppercase tracking-[0.34em] text-[#8A7440]"
                style={{ fontSize: "1vw" }}
              >
                {headerContext}
              </p>
            </div>
          </div>
          <p
            className="tabular font-semibold leading-none text-[#2A2318]"
            style={{ fontSize: "2vw" }}
          >
            {clock}
          </p>
        </header>

        <div className="mt-[2.4vh] flex items-center gap-[1.2vw]">
          <span
            className="font-invoice uppercase tracking-[0.16em] text-[#B08D45]"
            style={{ fontSize: "1.5vw" }}
          >
            Now Serving
          </span>
          <span className="h-[1px] flex-1 bg-gradient-to-r from-[#C5A059] to-transparent" />
        </div>

        <main className="mt-[2vh] min-h-0 flex-1">
          {!floors ? (
            <div className="flex h-full flex-col items-center justify-center">
              <Serving tickets={servingDetail.map((x) => x.label || x.ticket)} big />
              <div className="mt-[4vh]" />
              <Label style={{ fontSize: "1.2vw" }}>Waiting</Label>
              <div data-testid="display-waiting">
                <Waiting tickets={waitingDetail.map((x) => x.label || x.ticket)} big />
              </div>
            </div>
          ) : singleBig ? (
            <div className="flex h-full flex-col items-center justify-center" data-testid={`display-floor-${shownDepts[0].key}`}>
              <p
                className="font-invoice font-bold uppercase tracking-[0.2em] text-[#9A7822]"
                style={{ fontSize: "2.4vw" }}
              >
                {shownDepts[0].label}
              </p>
              <Label style={{ fontSize: "1vw" }}>{shownDepts[0].floor}</Label>
              <Label style={{ marginTop: "3vh", fontSize: "1.2vw" }}>Now serving</Label>
              <Serving tickets={forDept(servingDetail, shownDepts[0].key)} big />
              <Label style={{ marginTop: "3.4vh", fontSize: "1.2vw" }}>Waiting</Label>
              <Waiting tickets={forDept(waitingDetail, shownDepts[0].key)} big />
            </div>
          ) : (
            <div
              className="grid h-full gap-[2vw]"
              style={{ gridTemplateColumns: `repeat(${shownDepts.length}, minmax(0, 1fr))` }}
            >
              {shownDepts.map((d) => (
                <div
                  key={d.key}
                  className="flex flex-col items-center justify-center overflow-hidden rounded-[0.6vw] border border-[#E4D9BF] bg-white/55 px-[2vw] py-[2vh] text-center shadow-[0_1px_0_rgba(197,160,89,0.25)]"
                  data-testid={`display-floor-${d.key}`}
                >
                  <p
                    className="font-invoice font-bold uppercase tracking-[0.2em] text-[#9A7822]"
                    style={{ fontSize: "1.8vw", minHeight: "4.8vw", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center" }}
                  >
                    {d.label}
                  </p>
                  <Label style={{ fontSize: "0.9vw" }}>{d.floor}</Label>

                  <Label style={{ marginTop: "2.4vh" }}>Now serving</Label>
                  <Serving tickets={forDept(servingDetail, d.key)} />

                  <Label style={{ marginTop: "2.4vh" }}>Waiting</Label>
                  <Waiting tickets={forDept(waitingDetail, d.key)} />
                </div>
              ))}
            </div>
          )}
        </main>

        <footer className="mt-[2vh] border-t border-[#E4D9BF] pt-[1.4vh] text-center">
          <p className="text-[#6F6349]" style={{ fontSize: "1vw" }}>
            Please wait for your number to be called — thank you for your patience.
          </p>
        </footer>
      </div>
    </div>
  );
}
